import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { useCashApi } from '~Components/CashUniverse';
import NumberInput from '~ComponentsImproved/Numpad';
import { useReportsApi } from '~Components/ReportsUniverse';
import { useButton, useCurrency, useI18n, useMetadata, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { StyledH2 } from '~ComponentsImproved/Typography/Heading/styles';
import { PrintRow } from '~Components/PrinterManager';
import { BlackButton, Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { DateTime } from 'luxon';
import { useCashDrawer } from '~Providers/CashDrawerProvider';

const Wrapper = styled(Box)`
  max-width: 480px;
  text-align: center;
  margin: 200px auto;
`;

interface Props {
  isHandy?: boolean;
}

const DayNotStarted: FC<Props> = ({ isHandy }) => {
  const { i18n } = useI18n();
  const reportsApi = useReportsApi();
  const cashApi = useCashApi();
  const currency = useCurrency();
  const cashDrawer = useCashDrawer();

  const staff = useSelector((state) => state.staff);
  const metadata = useMetadata();

  const [storage] = useStorage();

  const [showCashFloat, setShowCashFloat] = useState<boolean>(false);

  const reportSnapshot = useSelector((state) => state.reportSnapshot);

  const handleSubmit = (digits?: string) => {
    setShowCashFloat(false);

    const startOfDayAmount = digits ? Math.round(parseFloat(digits) * 100) : 0;
    cashApi.startDay(startOfDayAmount);

    /* Print a slip with cash amount, timestamp, and device used.  
   Staff put this slip in the cash drawer to prevent end-of-day fraud. */
    window.printerManager?.initiatePrint({
      printType: 'START_DAY',
      data: [
        { content: ' ' },
        { content: ' ' },
        { content: ' ' },
        { content: ' ' },
        { content: ' ' },
        { content: i18n('DNS.Print.Title'), fontSize: 28, alignment: 'CENTER' },
        { content: DateTime.local().toFormat('HH:mm dd-MM-yy'), alignment: 'CENTER' },
        staff && { content: `${i18n('ParkedOrders.Staff')}: ${staff.name}`, alignment: 'CENTER' },
        { content: ' ' },
        {
          content: `${i18n('Report.CashRegisterIn')}: ` + currency.format(startOfDayAmount),
          alignment: 'CENTER',
        },
        { content: ' ' },
        {
          content: `${i18n('Menu.Device')}: ${storage.taxComplianceDeviceName ||
            'Unknown device'}`,
          alignment: 'CENTER',
        },
        { content: ' ' },
        { content: ' ' },
      ].filter(Boolean) as PrintRow[],
    });

    reportsApi.startDay();
  };

  const shouldNotEnterCash = metadata.data.hasIndividualEOD && storage.disableCash;

  const openRegisterButton = useButton({
    onClick: () => {
      if (shouldNotEnterCash) {
        handleSubmit();
        return;
      }

      setShowCashFloat(true);
      cashDrawer.open();
    },
  });

  if (reportSnapshot?.loading) {
    return null;
  }

  return (
    <>
      {showCashFloat && (
        <NumberInput
          decimal
          submitText={i18n('DNS.confirmCashIn')}
          onClose={() => setShowCashFloat(false)}
          onSubmit={handleSubmit}
        />
      )}

      <Box
        fluid
        display="flex"
        alignItems="center"
        justifyContent="center"
        padding={isHandy ? 'large' : undefined}>
        <Wrapper display="flex" flexDirection="column" justifyContent="center" gap="xs">
          <Heading level={isHandy ? 2 : 1} color="var(--colorWhite)">
            {i18n('POS.RegisterClosed')}
          </Heading>

          <Paragraph level={isHandy ? 4 : 2} color="var(--colorBlueGray200)">
            {metadata.data.hasIndividualEOD
              ? i18n('DNS.DeviceSpecificEOD.OpenIndividual')
              : i18n('DNS.DeviceSpecificEOD.OpenAll')}
          </Paragraph>

          <BlackButton marginTop="small" {...openRegisterButton}>
            {i18n('DNS.openRegister')}
          </BlackButton>
        </Wrapper>
      </Box>
    </>
  );
};

export default DayNotStarted;
